import { Link } from '@/components/link';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  CheckRounded,
  ContentCopyRounded,
  Error as ErrorIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
} from '@/constants/hardfin-standard-icons';
import { LocationDataUploadResult } from '@/location/api/upload-location-data';
import type { ExpandedLocationsState } from '@/location/components/location-data-upload/types';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { SparseLocation } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  createIdentifierSuffix,
  formatFailedRowsForCopy,
} from '@/utilities/upload-helpers';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FixedSizeList as VirtualList } from 'react-window';

const ITEM_HEIGHT = 40;
const FAILED_ROW_HEIGHT = 80;

interface ResultsProps {
  uploadResult: LocationDataUploadResult | null;
  expandedLocations: ExpandedLocationsState;
  onToggleExpanded: (section: keyof ExpandedLocationsState) => void;
  onBack: () => void;
  onStartNew: () => void;
  onClose: () => void;
}

interface RowFailure {
  rowNumber: number;
  locationName: string;
  errors: string[];
}

interface LocationRowProps {
  index: number;
  style: React.CSSProperties;
  data: SparseLocation[];
}

const LocationRow = ({ index, style, data }: LocationRowProps) => {
  const location = data[index];
  return (
    <div style={style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={
            <Link
              to={`/locations/$locationKey`}
              params={{ locationKey: location.key }}
            >
              {getEffectiveLocationName(location)}
            </Link>
          }
        />
      </ListItem>
    </div>
  );
};

interface FailedRowItemProps {
  index: number;
  style: React.CSSProperties;
  data: RowFailure[];
}

const FailedRowItem = ({ index, style, data }: FailedRowItemProps) => {
  const row = data[index];
  return (
    <div style={style}>
      <ListItem component="div" dense>
        <ListItemText
          primary={`For file row ${row.rowNumber}${row.locationName ? ` (location "${row.locationName}")` : ''}:`}
          secondary={
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {row.errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          }
        />
      </ListItem>
    </div>
  );
};

export function Results(props: ResultsProps) {
  const [isCopied, setIsCopied] = useState(false);

  if (!props.uploadResult) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">
            No upload results available. Please try again.
          </Typography>
          <Button variant="contained" onClick={props.onStartNew} sx={{ mt: 2 }}>
            Start new upload
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { success, items, structureErrors, rowErrors } = props.uploadResult;

  const locationsCreated: SparseLocation[] = [];
  const locationsUpdated: SparseLocation[] = [];
  const locationsNotUpdated: SparseLocation[] = [];
  const failedRows: RowFailure[] = [];

  /**
   * Handles copying all failed row errors to clipboard with visual feedback
   */
  const handleCopyErrors = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion toggle when clicking copy
    try {
      const text = formatFailedRowsForCopy(failedRows, (row) =>
        createIdentifierSuffix(row, 'locationName', 'location')
      );
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy errors:', error);
    }
  };

  rowErrors.forEach((row) => {
    if (row.errors.length > 0) {
      failedRows.push({
        rowNumber: row.index,
        locationName: '',
        errors: row.errors.map(
          (err) => `Column "${err.fieldName}" has an error: ${err.error}`
        ),
      });
      return;
    }
  });

  items.forEach((item) => {
    if (item.errors.length > 0) {
      failedRows.push({
        rowNumber: item.index,
        locationName: item.locationName,
        errors: item.errors.map(
          (err) => `Column "${err.fieldName}" has an error: ${err.error}`
        ),
      });
      return;
    }
    if (item.validatedLocation) {
      if (item.noOp) {
        locationsNotUpdated.push(item.validatedLocation);
      } else if (item.created && item.validatedLocation) {
        locationsCreated.push(item.validatedLocation);
      } else if (item.updated && item.validatedLocation) {
        locationsUpdated.push(item.validatedLocation);
      }
    }
  });
  const totalProcessed =
    locationsCreated.length +
    locationsUpdated.length +
    locationsNotUpdated.length;
  return (
    <Card>
      <CardContent>
        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              <AlertTitle>Success</AlertTitle>
              <Typography variant="body2">
                Your location data uploaded successfully for{' '}
                {pluralize(totalProcessed, {
                  singular: 'location',
                  plural: 'locations',
                })}
              </Typography>
            </Alert>

            {locationsCreated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                    cursor: locationsCreated.length ? 'pointer' : undefined,
                  }}
                  onClick={
                    locationsCreated.length
                      ? () => props.onToggleExpanded('created')
                      : undefined
                  }
                >
                  <Box display="flex" alignItems="center">
                    <AddIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Locations created ({formatNumber(locationsCreated.length)}
                      )
                    </Typography>
                  </Box>
                  {locationsCreated.length > 0 &&
                    (props.expandedLocations.created ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    ))}
                </Box>
                <Collapse in={props.expandedLocations.created}>
                  <VirtualList
                    height={Math.min(
                      locationsCreated.length * ITEM_HEIGHT,
                      200
                    )}
                    itemCount={locationsCreated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={locationsCreated}
                    width="100%"
                  >
                    {LocationRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}

            {locationsUpdated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'success.light',
                    color: 'success.contrastText',
                    cursor: locationsUpdated.length ? 'pointer' : undefined,
                  }}
                  onClick={
                    locationsUpdated.length
                      ? () => props.onToggleExpanded('updated')
                      : undefined
                  }
                >
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Locations updated ({formatNumber(locationsUpdated.length)}
                      )
                    </Typography>
                  </Box>
                  {locationsUpdated.length > 0 &&
                    (props.expandedLocations.updated ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    ))}
                </Box>
                <Collapse in={props.expandedLocations.updated}>
                  <VirtualList
                    height={Math.min(
                      locationsUpdated.length * ITEM_HEIGHT,
                      200
                    )}
                    itemCount={locationsUpdated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={locationsUpdated}
                    width="100%"
                  >
                    {LocationRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}

            {locationsNotUpdated.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: (theme) => theme.palette.grey.A400,
                    color: 'info.contrastText',
                    cursor: locationsNotUpdated.length ? 'pointer' : undefined,
                  }}
                  onClick={
                    locationsNotUpdated.length
                      ? () => props.onToggleExpanded('notUpdated')
                      : undefined
                  }
                >
                  <Box display="flex" alignItems="center">
                    <InfoIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Locations with no updates (
                      {formatNumber(locationsNotUpdated.length)})
                    </Typography>
                  </Box>
                  {locationsNotUpdated.length > 0 &&
                    (props.expandedLocations.notUpdated ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    ))}
                </Box>
                <Collapse in={props.expandedLocations.notUpdated}>
                  <VirtualList
                    height={Math.min(
                      locationsNotUpdated.length * ITEM_HEIGHT,
                      200
                    )}
                    itemCount={locationsNotUpdated.length}
                    itemSize={ITEM_HEIGHT}
                    itemData={locationsNotUpdated}
                    width="100%"
                  >
                    {LocationRow}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {structureErrors.length > 0 && (
                <>
                  <AlertTitle>File format invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file has column names that do not match the
                    required location data fields
                  </Typography>
                </>
              )}
              {failedRows.length > 0 && (
                <>
                  <AlertTitle>Uploaded data invalid</AlertTitle>
                  <Typography variant="body2">
                    The uploaded file includes data that does not match your
                    locations or field requirements
                  </Typography>
                </>
              )}
            </Alert>
            {structureErrors && structureErrors.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('structureErrors')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Mismatched columns ({formatNumber(structureErrors.length)}
                      )
                    </Typography>
                  </Box>
                  {props.expandedLocations.structureErrors ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </Box>
                <Collapse in={props.expandedLocations.structureErrors}>
                  <ul
                    style={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      listStyleType: 'disc',
                    }}
                  >
                    {structureErrors.map((row) => (
                      <li
                        key={`structure-${row.fieldName}`}
                        style={{ paddingBottom: '8px' }}
                      >
                        Column "{`${row.fieldName}`}" not found
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </Paper>
            )}
            {failedRows && failedRows.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                    cursor: 'pointer',
                  }}
                  onClick={() => props.onToggleExpanded('failed')}
                >
                  <Box display="flex" alignItems="center">
                    <ErrorIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      Invalid rows ({formatNumber(failedRows.length)}{' '}
                      {pluralize(failedRows.length, {
                        includeCount: false,
                        singular: 'location',
                        plural: 'locations',
                      })}
                      )
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={isCopied ? 'Copied!' : 'Copy all errors'}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={handleCopyErrors}
                        aria-label={isCopied ? 'Copied' : 'Copy all errors'}
                        sx={{
                          color: 'error.contrastText',
                          '&:hover': {
                            bgcolor: 'error.dark',
                          },
                        }}
                      >
                        {isCopied ? (
                          <CheckRounded fontSize="small" />
                        ) : (
                          <ContentCopyRounded fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    {props.expandedLocations.failed ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </Box>
                </Box>
                <Collapse in={props.expandedLocations.failed}>
                  <VirtualList
                    height={Math.min(
                      failedRows.length * FAILED_ROW_HEIGHT,
                      300
                    )}
                    itemCount={failedRows.length}
                    itemSize={FAILED_ROW_HEIGHT}
                    itemData={failedRows}
                    width="100%"
                  >
                    {FailedRowItem}
                  </VirtualList>
                </Collapse>
              </Paper>
            )}
          </>
        )}

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button variant="outlined" onClick={props.onBack}>
            Back
          </Button>
          <Box>
            <Button
              variant="outlined"
              onClick={props.onStartNew}
              sx={{ mr: 1 }}
            >
              Start new upload
            </Button>
            <Button variant="contained" onClick={props.onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
